import React, { useState, useEffect } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export default function MedicationEMARModal({ serverId, serviceUserId, onClose }) {
    const [medications, setMedications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchMedications = async () => {
            setLoading(true);
            try {
                const ref = collection(db, 'servers', serverId, 'serviceUsers', serviceUserId, 'myMedications');
                const snap = await getDocs(query(ref));
                
                const loaded = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                loaded.sort((a, b) => {
                    const statusA = a.status === 'active' || a.active !== false ? 0 : 1;
                    const statusB = b.status === 'active' || b.active !== false ? 0 : 1;
                    if (statusA !== statusB) return statusA - statusB;
                    
                    const nameA = (a.medicationName || a.name || '').toLowerCase();
                    const nameB = (b.medicationName || b.name || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                });
                
                setMedications(loaded);
            } catch (err) {
                console.error("Error fetching medications:", err);
                setError("Failed to load medication records.");
            } finally {
                setLoading(false);
            }
        };

        if (serverId && serviceUserId) {
            fetchMedications();
        }
    }, [serverId, serviceUserId]);

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '20px', color: '#111827' }}>Medication (EMAR)</h2>
                        <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>Electronic Medication Administration Records</p>
                    </div>
                    <button onClick={onClose} style={styles.closeBtn}>×</button>
                </div>

                <div style={styles.scrollArea}>
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading medications...</div>
                    ) : error ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>{error}</div>
                    ) : medications.length === 0 ? (
                        <div style={styles.emptyState}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>💊</div>
                            <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No Medications</h3>
                            <p style={{ margin: 0, color: '#6b7280' }}>There are no medications currently prescribed to this service user.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {medications.map((med, index) => {
                                const name = med.medicationName || med.name || 'Unknown Medication';
                                const isActive = med.status === 'active' || med.active !== false;
                                
                                return (
                                    <div key={med.id || index} style={{...styles.recordCard, opacity: isActive ? 1 : 0.6}}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                            <div>
                                                <h3 style={{ margin: 0, fontSize: '16px', color: '#111827' }}>{name}</h3>
                                                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                                                    {med.dosage || 'Dosage not specified'} • {med.route || 'Route not specified'}
                                                </div>
                                            </div>
                                            <span style={{ 
                                                display: 'inline-block', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 'bold',
                                                backgroundColor: isActive ? '#fce7f3' : '#f3f4f6', 
                                                color: isActive ? '#9d174d' : '#6b7280', 
                                                border: `1px solid ${isActive ? '#fbcfe8' : '#d1d5db'}`
                                            }}>
                                                {isActive ? 'PRESCRIBED' : 'STOPPED'}
                                            </span>
                                        </div>
                                        
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
                                            <div style={{ flex: '1', minWidth: '150px' }}>
                                                <label style={styles.fieldLabel}>Frequency</label>
                                                <div style={styles.fieldValue}>{med.frequency || 'As directed'}</div>
                                            </div>
                                            <div style={{ flex: '1', minWidth: '150px' }}>
                                                <label style={styles.fieldLabel}>Start Date</label>
                                                <div style={styles.fieldValue}>{med.startDate ? new Date(med.startDate?.seconds ? med.startDate.seconds * 1000 : med.startDate).toLocaleDateString() : 'N/A'}</div>
                                            </div>
                                            {!isActive && (
                                                <div style={{ flex: '1', minWidth: '150px' }}>
                                                    <label style={styles.fieldLabel}>End Date</label>
                                                    <div style={styles.fieldValue}>{med.endDate ? new Date(med.endDate?.seconds ? med.endDate.seconds * 1000 : med.endDate).toLocaleDateString() : 'N/A'}</div>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div style={{ marginTop: '16px' }}>
                                            <button style={styles.viewBtn} onClick={() => alert('View detailed MAR chart: Coming to React V2 soon.')}>
                                                View MAR Chart
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
    modalHeader: { padding: '24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    closeBtn: { background: 'none', border: 'none', fontSize: '24px', color: '#9ca3af', cursor: 'pointer', lineHeight: 1 },
    scrollArea: { padding: '24px', overflowY: 'auto', flex: 1, backgroundColor: '#f9fafb' },
    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0', textAlign: 'center', backgroundColor: 'white', borderRadius: '8px', border: '1px dashed #d1d5db' },
    recordCard: { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
    fieldLabel: { display: 'block', fontSize: '11px', textTransform: 'uppercase', color: '#6b7280', marginBottom: '4px', fontWeight: 'bold' },
    fieldValue: { fontSize: '14px', color: '#111827' },
    viewBtn: { width: '100%', padding: '10px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', color: '#374151', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }
};
